import { Alert, Snackbar, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, useTheme, Typography } from '@mui/material';
import { FC, useContext, useEffect, useState } from 'react';
import { MadeInStoreItem } from '../../../../app/services/api/generated';
import MadeInStoreItemRow from './MadeInStoreItemRow';
import { useTranslation } from 'react-i18next';
import CreateFinishedGoodsItemDialog from './CreateFinishedGoodsItemDialog';
import { InfoCircle } from '../../../../assets';
import { UserContext } from '../../../../components/shared/useUser';
import { TableHeader } from '../../../../components/forms/TableHeader';
import { Pill } from '../../../../components/shared/Pill';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { TableLoading } from '../../../../components/forms/TableLoading';
import { EmptyRows } from '../../../../components/forms/EmptyRows';

type Props = {
  list: MadeInStoreItem[];
  isLoading: boolean;
  totalCount: number;
  onClear: () => void;
  onCreateNew: () => void;
  lastSearchTerm: string;
}

const MadeInStoreList: FC<Props> = ({ list, totalCount, isLoading, onClear, onCreateNew, lastSearchTerm }) => {
  const { t } = useTranslation('recipe');
  const theme = useTheme();
  const { hasPermissionTo } = useContext(UserContext);
  const [hasPermissionToWriteMadeInStore, setHasPermissionToWriteMadeInStore] = useState<boolean>(false);
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
  const [showSubmitStatusMessage, setShowSubmitStatusMessage] = useState<boolean>(false);
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState<boolean>(false);
  const [currentItem, setCurrentItem] = useState<MadeInStoreItem | undefined>();

  useEffect(() => {
    setHasPermissionToWriteMadeInStore(hasPermissionTo(['MadeInStoreWrite']));
  }, []);

  const onCreateItem = (item: MadeInStoreItem) => {
    setShowCreateDialog(true);
    setCurrentItem(item);
  };

  const onConfirm = (submittedSuccessfully: boolean) => {
    setShowCreateDialog(false);
    setShowSubmitStatusMessage(true);
    setIsSubmittedSuccessfully(submittedSuccessfully);
    setCurrentItem({});
  };

  const onCancelCreate = () => {
    setShowCreateDialog(false);
    setCurrentItem({});
  };

  if (totalCount == 0 && !isLoading) {
    return <EmptyTable bodyRowHeight={40}
      headerHeight={45}
      text={t('items')}
      searchTerm={lastSearchTerm}
      onClear={onClear}
      onAdd={onCreateNew}
      onAddIsCreating={false}
      addItemText={t('createNew')}
      onAddIsDisabled={false}
      withPaginationShown={false} />;
  }

  return (
    <>
      <TableHeader>
        <Typography variant="textMD"
          fontWeight={600}>
          Items
        </Typography>
        {(totalCount ?? 0) > 0 && <Pill text={`${totalCount} Items`} />}
      </TableHeader>
      {isLoading ? <TableLoading headerHeight={45}
        bodyRowHeight={40}
        minTableWidth={1000} /> :
        <TableContainer>
          <Table sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px', minWidth: '1000px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('itemNumber')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('itemDescription')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}
                  align='center'>
                  {t('stockOnHand')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}
                  align='center'>
                  {t('maximumAmount')}
                  <Tooltip title={t('maximumAmountInfo')}>
                    <SvgIcon sx={{ width: '15px', ml: '5px', cursor: 'pointer', verticalAlign: 'top' }}><InfoCircle color={theme.palette.custom.blue[500]} /></SvgIcon>
                  </Tooltip>
                </TableCell>
                {hasPermissionToWriteMadeInStore && <TableCell sx={{ whiteSpace: 'nowrap' }}
                  align='center'>
                  {t('createFinishedGoodsItem')}
                  <Tooltip title={t('createFinishedGoodsItemInfo')}>
                    <SvgIcon sx={{ width: '15px', ml: '5px', cursor: 'pointer', verticalAlign: 'top' }}><InfoCircle color={theme.palette.custom.blue[500]} /></SvgIcon>
                  </Tooltip>
                </TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row) => (
                <MadeInStoreItemRow key={row.id}
                  item={row}
                  onCreateItem={onCreateItem}
                  hasPermissionToWriteMadeInStore={hasPermissionToWriteMadeInStore}
                />
              ))}
              <EmptyRows numberOfCurrentRows={list.length}
                height={40} />
            </TableBody>
          </Table>
        </TableContainer>}
      <CreateFinishedGoodsItemDialog
        showDialog={showCreateDialog}
        madeInStoreItem={currentItem}
        onConfirm={onConfirm}
        onCancel={onCancelCreate}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showSubmitStatusMessage}
        onClose={() => setShowSubmitStatusMessage(false)}
        autoHideDuration={5000}
      >
        <Alert
          onClose={() => setShowSubmitStatusMessage(false)}
          severity={isSubmittedSuccessfully ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {isSubmittedSuccessfully ? t('successMessage') : t('errorMessage')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MadeInStoreList;